import axios from 'axios';

// Configure base URL based on environment or default to current origin
const API_URL = 'https://api.devstc.ispcorp.pe';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add the auth token to every request
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            // Clear local storage and redirect to login if unauthorized
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('permissions');

            // Only redirect if not already on login page
            if (!window.location.pathname.includes('/login')) {
                const url = error.config ? error.config.url : 'unknown';
                const status = error.response ? error.response.status : 'no-status';
                window.location.href = `/login?expired=true&from=${encodeURIComponent(url)}&status=${status}`;
            }
        }
        return Promise.reject(error);
    }
);

export const authService = {
    login: async (username, password) => {
        const response = await api.post('/login.php', { username, password, portal: 'agent' });
        if (response.data.status === 'success') {
            localStorage.setItem('token', response.data.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.data.user));
            localStorage.setItem('permissions', JSON.stringify(response.data.data.permissions));
        }
        return response.data;
    },
    logout: async () => {
        try {
            await api.post('/pbx/agents/session_end.php');
        } catch (e) { console.error("Logout session end error", e); }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        // window.location.href = '/login'; // REMOVED: Managed by Context/React Router now
        return true;
    },
    getNavigation: async () => {
        const response = await api.get('/navigation.php');
        return response.data;
    },
    pingSession: async () => {
        try {
            await api.get('/session_ping.php');
        } catch (error) {
            console.error("Session ping failed", error);
        }
    }
};

export const pbxService = {
    getBreaks: async () => {
        const response = await api.get('/pbx/breaks/index.php?all=true');
        return response.data;
    },
    getActiveCampaigns: async () => {
        const response = await api.get('/pbx/campaigns/active_by_agent.php');
        return response.data;
    },
    getRecentCalls: async () => {
        const response = await api.get('/pbx/calls/recent.php');
        return response.data;
    },

    startAgentSession: async () => {
        const response = await api.post('/pbx/agents/session_start.php');
        return response.data;
    },
    updateAgentStatus: async (status, breakId = null) => {
        const response = await api.post('/pbx/agents/status_update.php', { status, break_id: breakId });
        return response.data;
    },
    endAgentSession: async () => {
        const response = await api.post('/pbx/agents/session_end.php');
        return response.data;
    },
    getCurrentSession: async () => {
        const response = await api.get('/pbx/agents/session_current.php');
        return response.data;
    },
    getStateSnapshot: async () => {
        const response = await api.get('/pbx/agents/state_snapshot.php');
        return response.data;
    },
    getDashboardStats: async () => {
        const response = await api.get('/pbx/agents/dashboard_stats.php');
        return response.data;
    },

    saveForm: async (callId, formData, contactStatus = null, campaignContactId = null) => {
        const response = await api.post('/pbx/calls/save_form.php', {
            call_id: callId,
            form_data: formData,
            contact_status: contactStatus,
            campaign_contact_id: campaignContactId
        });
        return response.data;
    },

    updateCallStatus: async (callId, status) => {
        const response = await api.post('/pbx/calls/update_status.php', { call_id: callId, status });
        return response.data;
    },
    getQueueMonitoring: async () => {
        const response = await api.get('/pbx/agents/queue_monitoring.php');
        return response.data;
    },
    getNextContact: async (campaignId) => {
        const response = await api.get(`/pbx/campaigns/participants/next.php?campaign_id=${campaignId}`);
        return response.data;
    },
    getRescheduledParticipants: async (campaignId = null) => {
        let url = '/pbx/campaigns/participants/list_rescheduled.php';
        if (campaignId) url += `?campaign_id=${campaignId}`;
        const response = await api.get(url);
        return response.data;
    },
    reschedule: async (campaign_contact_id, reschedule_at) => {
        const response = await api.post('/pbx/campaigns/participants/reschedule.php', {
            campaign_contact_id,
            reschedule_at
        });
        return response.data;
    },
    releaseContact: async (campaign_contact_id) => {
        const response = await api.post('/pbx/campaigns/participants/release.php', { campaign_contact_id });
        return response.data;
    },
    createAgentOriginContact: async (campaignId, phone, participant = null) => {
        const response = await api.post('/pbx/campaigns/contacts/create_agent_origin.php', {
            campaign_id: campaignId,
            phone,
            participant
        });
        return response.data;
    },
    updateCampaignContactStatus: async (campaignContactId, status, rescheduleDate = null) => {
        const response = await api.post('/pbx/campaigns/contacts/update_status.php', {
            campaign_contact_id: campaignContactId,
            status,
            reschedule_date: rescheduleDate
        });
        return response.data;
    }
};

export const cardService = {
    create: async (participantUuid, cardData) => {
        const response = await api.post('/participants/cards.php', {
            participant_uuid: participantUuid,
            ...cardData
        });
        return response.data;
    }
};

export const tagService = {
    listAll: async () => {
        const response = await api.get('/tags/list.php');
        return response.data;
    }
};

export const participantService = {
    create: async (participantData) => {
        const response = await api.post('/participants/create.php', participantData);
        return response.data;
    },
    searchUbigeo: async (query) => {
        const response = await api.get(`/participants/ubigeo_search.php?q=${query}`);
        return response.data;
    },
    getFilters: async () => {
        const response = await api.get('/participants/get_filters.php');
        return response.data;
    },
    search: async (query, signal = null) => {
        const response = await api.get(`/participants/list.php?search=${query}`, { signal });
        return response.data;
    },
    associate: async (associationData) => {
        const response = await api.post('/participants/associate.php', associationData);
        return response.data;
    },
    getParticipantDetail: async (uuid) => {
        const response = await api.get(`/participants/detail.php?uuid=${uuid}`);
        return response.data;
    }
};

export const userService = {
    getSipCredentials: async () => {
        const response = await api.get('/pbx/get_profile_sip.php');
        return response.data;
    }
};

export const reportService = {
    getCDR: async (params) => {
        const response = await api.get('/pbx/reports/cdr.php', { params });
        return response.data;
    },
    getCDRExportUrl: (params) => {
        // Construct URL with auth token since it's a direct window.open/href
        const token = localStorage.getItem('token');

        // Filter out undefined, null, or empty string values to prevent "undefined" string in query
        const cleanParams = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v != null && v !== '' && v !== 'undefined')
        );

        const queryString = new URLSearchParams({
            ...cleanParams,
            action: 'export',
            token: token
        }).toString();
        return `${API_URL}/pbx/reports/cdr.php?${queryString}`;
    },
    getCallDetail: async (id) => {
        const response = await api.get('/pbx/reports/cdr.php', { params: { action: 'detail', id } });
        return response.data;
    },
    getRecordingUrl: (id, download = false) => {
        const token = localStorage.getItem('token');
        return `${API_URL}/pbx/reports/cdr.php?action=recording&id=${id}&download=${download ? '1' : '0'}&token=${token}`;
    }
};

export const pbxReportCatalogService = {
    getCampaigns: async () => {
        // For agents, use the active_by_agent endpoint which doesn't require admin permissions
        const response = await api.get('/pbx/campaigns/active_by_agent.php');
        return response.data;
    },
    // Agent doesn't need getUsersWithExtension as filters are removed
};

export default api;
